import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Inbox, PhoneCall, LogOut, Menu, UserCircle2, KeyRound } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuth';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { ChangePasswordDialog } from './ChangePasswordDialog';

const NAV = [
  { to: '/dashboard/leads', label: 'Leads', Icon: Inbox },
  { to: '/dashboard/calls', label: 'Calls', Icon: PhoneCall },
];

const SidebarBody = ({ onNavigate, onPassword }) => {
  const { user, logout } = useAdminAuth();
  return (
    <div className="flex h-full flex-col bg-[#0E1F3D] text-white">
      <div className="px-6 pt-8 pb-7 border-b border-white/10">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F58220]">Bruce Nelson P&H</p>
        <p className="mt-1 font-oswald text-2xl uppercase leading-tight">Admin<br /><span className="text-slate-400 text-lg">Console</span></p>
      </div>
      <nav className="flex-1 px-3 py-6 space-y-1" data-testid="admin-nav">
        {NAV.map(({ to, label, Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            data-testid={`admin-nav-${label.toLowerCase()}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-lg px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] transition-colors ${isActive ? 'bg-[#F58220]/15 text-[#F58220] ring-1 ring-[#F58220]/40' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`}
          >
            <Icon className="h-4 w-4" />{label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-4 space-y-3">
        <div className="flex items-center gap-3 px-2">
          <UserCircle2 className="h-7 w-7 text-slate-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate" data-testid="admin-user-name">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
        </div>
        <button onClick={onPassword} className="flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-slate-300 hover:bg-white/5" data-testid="admin-change-password-btn">
          <KeyRound className="h-4 w-4" />Password
        </button>
        <button onClick={logout} className="flex w-full items-center gap-3 rounded-lg bg-white/5 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-slate-200 hover:bg-white/10" data-testid="admin-logout-btn">
          <LogOut className="h-4 w-4" />Sign out
        </button>
      </div>
    </div>
  );
};

export const AdminLayout = () => {
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#F4F6F9] font-sans text-slate-800" data-testid="admin-layout">
      <Helmet><title>Admin | Bruce Nelson Plumbing & Heating</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <aside className="fixed inset-y-0 left-0 hidden w-60 lg:block"><SidebarBody onPassword={() => setPwOpen(true)} /></aside>
      <header className="sticky top-0 z-30 flex items-center justify-between bg-[#0E1F3D] px-4 py-3 text-white lg:hidden">
        <p className="font-oswald uppercase tracking-wide">BNPH Admin</p>
        <button onClick={() => setOpen(true)} aria-label="Open menu" data-testid="admin-mobile-menu-btn"><Menu className="h-6 w-6" /></button>
      </header>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-64 p-0 border-0">
          <SheetTitle className="sr-only">Admin menu</SheetTitle>
          <SidebarBody onNavigate={() => setOpen(false)} onPassword={() => { setOpen(false); setPwOpen(true); }} />
        </SheetContent>
      </Sheet>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <main className="lg:pl-60">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-8 lg:py-10"><Outlet /></div>
      </main>
    </div>
  );
};
